import { type HTMLAttributes, type ReactNode } from 'react';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'accent' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  leftIcon?: ReactNode;
  children: ReactNode;
}

const variantStyles = {
  default: `
    bg-surface-100 text-surface-700
    dark:bg-surface-700 dark:text-surface-300
  `,
  primary: `
    bg-primary-100 text-primary-700
    dark:bg-primary-900/30 dark:text-primary-300
  `,
  accent: `
    bg-accent-100 text-accent-700
    dark:bg-accent-900/30 dark:text-accent-300
  `,
  success: `
    bg-success-50 text-success-600
    dark:bg-success-900/30 dark:text-success-400
  `,
  warning: `
    bg-warning-50 text-warning-600
    dark:bg-warning-900/30 dark:text-warning-400
  `,
  danger: `
    bg-danger-50 text-danger-600
    dark:bg-danger-900/30 dark:text-danger-400
  `,
};

const dotColors = {
  default: 'bg-surface-500',
  primary: 'bg-primary-500',
  accent: 'bg-accent-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export function Badge({
  variant = 'default',
  size = 'md',
  dot = false,
  removable = false,
  onRemove,
  leftIcon,
  children,
  className = '',
  ...props
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        font-medium rounded-full
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`} />
      )}
      {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      {children}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="
            -mr-1 ml-0.5 p-0.5 rounded-full
            hover:bg-black/10 dark:hover:bg-white/10
            transition-colors duration-150
            focus:outline-none focus-visible:ring-2 focus-visible:ring-current
          "
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

// Numeric badge (for notifications, counts, etc.)
interface CountBadgeProps {
  count: number;
  max?: number;
  variant?: 'primary' | 'accent' | 'danger';
  size?: 'sm' | 'md';
  className?: string;
}

export function CountBadge({
  count,
  max = 99,
  variant = 'danger',
  size = 'sm',
  className = '',
}: CountBadgeProps) {
  if (count === 0) return null;

  const displayCount = count > max ? `${max}+` : count.toString();

  const colors = {
    primary: 'bg-primary-500',
    accent: 'bg-accent-500',
    danger: 'bg-danger-500',
  };

  const sizes = {
    sm: 'min-w-[18px] h-[18px] text-[10px]',
    md: 'min-w-[22px] h-[22px] text-xs',
  };

  return (
    <span
      className={`
        inline-flex items-center justify-center
        px-1.5 rounded-full
        font-bold text-white
        ${colors[variant]}
        ${sizes[size]}
        ${className}
      `}
    >
      {displayCount}
    </span>
  );
}

export default Badge;
